import { CoreEvent, PausedReason, WaitingReason } from "@nrk/player-core/types.js";
import { PlayerEvents } from "./player-events.js";
import { SubtitleState } from "./subtitles-state.js";
import { ReactiveState } from "@nrk/reactive-element";
const SEEKABLE_STEP_PERCENT = 0.02;
function PlaybackStateMixin(Base) {
  return class extends Base {
    static get reactiveAttributes() {
      return {
        ...super.reactiveAttributes,
        muted: Boolean
      };
    }
    static get reactiveProperties() {
      return {
        ...super.reactiveProperties,
        activeSession: Object,
        currentLiveProgramTitle: String,
        fullscreenState: Object,
        isCurrentTimeLive: Boolean,
        isAudioOnly: Boolean,
        isEnded: Boolean,
        isLive: Boolean,
        isPlaying: Boolean,
        isRealtime: Boolean,
        isSeeking: Boolean,
        isStarted: Boolean,
        isTimeRunning: Boolean,
        isWaiting: Boolean,
        manifest: Object,
        pipState: Object,
        playbackRate: Number,
        remotePlaybackState: Object,
        seekableStep: Number,
        subtitleState: ReactiveState,
        volume: Number
      };
    }
    /** The Player-Core PlaybackSession instance for current source */
    activeSession;
    currentLiveProgramTitle;
    /** The current Fullscreen state reported by Player-Core */
    fullscreenState;
    /** Is the "video" only audio with a single image as video */
    isAudioOnly = false;
    /** Are we watching the stream live */
    isCurrentTimeLive = true;
    /** Max time in seconds currentTime (playhead) can be from `endTime` and still be considered as live (not reactive)*/
    isCurrentTimeLiveCutOffTime = 15;
    /** Flag indicating that media playback has completed */
    isEnded = false;
    /** Flag indicating that media playback is a channel or event */
    isLive = false;
    /** Flag indicating that media playback has started */
    isPlaying = false;
    /**
     * Flag indicating that a stream is broadcasting in real time.
     * During a live event, this will change to "false" when the event has completed.
     */
    isRealtime = false;
    /** Flag indicating that we are seeking for new media playback position */
    isSeeking = false;
    /** Flag indicating that media playback has started */
    isStarted = false;
    /** Flag indicating that media playback is ongoing */
    isTimeRunning = false;
    /** Flag indicating that media playback has temporarily stopped pending more data */
    isWaiting = false;
    manifest;
    /** Flag indicating that audio is muted */
    muted = false;
    /** The current Picture-In-Picture state reported by Player-Core */
    pipState;
    /** Playback speed where 1.0 is "normal" */
    playbackRate = 1;
    /** The current Remote-Playback (airplay) state reported by Player-Core */
    remotePlaybackState;
    /** The seekable step size in seconds derived from the current MediaRange */
    seekableStep = 1;
    /** All things regarding subtitles */
    subtitleState;
    /** The audio volume level from 0 to 1 */
    volume = 1;
    preferredSubtitle;
    updating(propName, oldValue) {
      super.updating(propName, oldValue);
      const isCurrentTimeLiveDependencies = ["isPlaying", "isRealtime", "isSeeking", "isWaiting"];
      if (isCurrentTimeLiveDependencies.includes(propName)) {
        this._updateCurrentTimeIsLive();
      }
    }
    _observePlaybackSession(session) {
      this.activeSession = session;
      this.fullscreenState = session.getFullscreenState();
      this.pipState = session.getPiPState();
      this.remotePlaybackState = session.getRemotePlaybackState();
      this.isPlaying = session.isPlaying();
      this.isWaiting = session.isWaiting();
      session.on(CoreEvent.Broken, this._onBroken, this);
      session.on(CoreEvent.FullscreenStateChange, this._onFullscreenStateChange, this);
      session.on(CoreEvent.LiveStreamEnded, this._onLiveStreamEnded, this);
      session.on(CoreEvent.MediaInfoLoaded, this._onMediaInfoLoaded, this);
      session.on(CoreEvent.MediaRangeChange, this._onMediaRangeChange, this);
      session.on(CoreEvent.MutedChange, this._onMutedChange, this);
      session.on(CoreEvent.PiPStateChange, this._onPipStateChange, this);
      session.on(CoreEvent.PlayingChange, this._onPlayingChange, this);
      session.on(CoreEvent.RateChange, this._onRateChange, this);
      session.on(CoreEvent.RemotePlaybackStateChange, this._onRemotePlaybackStateChange, this);
      session.once(CoreEvent.ScrubberUpdate, this._onScrubberUpdate, this);
      session.on(CoreEvent.TimeRunningChange, this._onTimeRunningChange, this);
      session.on(CoreEvent.VolumeChange, this._onVolumeChange, this);
      session.on(CoreEvent.WaitingChange, this._onWaitingChange, this);
      this.addEventListener(PlayerEvents.EPGChange, this._onEPGChange);
    }
    _unobservePlaybackSession(session) {
      if (session) {
        this.activeSession = void 0;
        this.isAudioOnly = false;
        this.isCurrentTimeLive = false;
        this.isEnded = false;
        this.isLive = false;
        this.isPlaying = false;
        this.isRealtime = false;
        this.isSeeking = false;
        this.isStarted = false;
        this.isTimeRunning = false;
        this.isWaiting = false;
        this.seekableStep = 1;
        this.subtitleState?.destroy();
        this.subtitleState = void 0;
        this.removeEventListener(PlayerEvents.EPGChange, this._onEPGChange);
        window.removeEventListener("beforeunload", this._onWindowUnload);
      }
    }
    _onEPGChange(event) {
      const customEvent = event;
      this.currentLiveProgramTitle = customEvent.detail ? customEvent.detail.title : void 0;
    }
    _onBroken(event) {
      if ("panic" in this && typeof this.panic === "function") {
        this.panic(event.error);
      } else {
        this.error = event.error;
      }
    }
    _onFullscreenStateChange(event) {
      this.fullscreenState = event.state;
      if (this.fullscreenState.isActive) {
        window.addEventListener("beforeunload", this._onWindowUnload);
      } else {
        window.removeEventListener("beforeunload", this._onWindowUnload);
      }
    }
    _onLiveStreamEnded() {
      this.isRealtime = false;
    }
    _onMediaInfoLoaded(event) {
      this.isAudioOnly = event.isAudioOnly ?? this.isAudioOnly;
      this.isLive = this.activeSession?.isLiveStream() ?? false;
      this.isRealtime = this.isLive && !this.activeSession?.isLiveStreamEnded();
      if (this.activeSession) {
        this.subtitleState = new SubtitleState(this.activeSession, this.manifest, this.preferredSubtitle);
      }
    }
    _onScrubberUpdate() {
      this.isStarted = true;
    }
    _onMediaRangeChange(event) {
      this.seekableStep = Math.round(Math.max(1, (event.end - event.start) * SEEKABLE_STEP_PERCENT));
    }
    _onMutedChange(muted) {
      this.muted = muted;
    }
    _onPipStateChange(event) {
      this.pipState = event.state;
    }
    _onPlayingChange(event) {
      this.isPlaying = event.isPlaying;
      this.isEnded = event.pausedReason === PausedReason.Ended;
      if (this.isEnded) {
        this.isStarted = false;
      }
    }
    _onRateChange(rate) {
      this.playbackRate = rate;
    }
    _onRemotePlaybackStateChange(event) {
      this.remotePlaybackState = event.state;
    }
    _onTimeRunningChange(event) {
      this.isTimeRunning = event.isTimeRunning;
      this.isEnded = false;
    }
    _onVolumeChange(event) {
      this.volume = event;
    }
    _onWaitingChange(event) {
      if (event.reason === WaitingReason.Seeking) {
        this.isSeeking = event.isWaiting;
      }
      this.isWaiting = event.isWaiting;
    }
    _onWindowUnload(_event) {
      document.exitFullscreen?.().catch((_err) => {
      });
    }
    _updateCurrentTimeIsLive() {
      if (!this.activeSession) {
        return;
      }
      if (!this.isRealtime || !this.isPlaying) {
        this.isCurrentTimeLive = false;
        return;
      }
      const scrubberEnd = this.activeSession.getScrubberRange().end;
      const scrubberEndOnePercent = Math.max(scrubberEnd / 100, 1);
      const scrubberEndFivePercent = scrubberEndOnePercent * 5;
      const currentTime = this.activeSession.getCurrentTime();
      const cutOffTime = Math.min(this.isCurrentTimeLiveCutOffTime, scrubberEndFivePercent);
      this.isCurrentTimeLive = scrubberEnd - currentTime < cutOffTime;
    }
  };
}
export {
  PlaybackStateMixin
};
